// The tax number on a billing profile, and the type it is sent under.
//
// The API takes `taxId.type` as a Stripe tax ID type for the country — eu_vat,
// gb_vat, al_tin and the rest — and infers it from the address when it is left
// out. The types named below are the ones the API documents; every other country
// is left to that inference rather than guessed at here.
//
// Kosovo is the exception that has to be handled on this side. Stripe has no tax
// ID type for it, so its NUI (the fiscal number) goes as `xk_nui`: the server
// stores it and prints it on the invoice as a custom field labelled NUI. Nothing
// else would get it onto an invoice at all.
//
// A tax number belongs on the billing profile only — it is never passed to the
// card form or into a card's billing details.

export const KOSOVO = "XK";
export const KOSOVO_TAX_ID_TYPE = "xk_nui";

const EU_MEMBER_STATES = [
   "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE",
   "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"
];

const TAX_ID_TYPES = {
   ...Object.fromEntries(EU_MEMBER_STATES.map((country) => [country, "eu_vat"])),
   GB: "gb_vat",
   AL: "al_tin",
   MK: "mk_vat",
   RS: "rs_pib",
   ME: "me_pib",
   BA: "ba_tin",
   [KOSOVO]: KOSOVO_TAX_ID_TYPE
};

// What the number is called where the business is, so the field asks for the
// number people there actually have.
const TAX_ID_LABELS = {
   ...Object.fromEntries(EU_MEMBER_STATES.map((country) => [country, "VAT number"])),
   GB: "VAT number",
   AL: "NIPT (tax number)",
   MK: "VAT number",
   RS: "PIB (tax number)",
   ME: "PIB (tax number)",
   BA: "Tax number (JIB)",
   [KOSOVO]: "NUI (fiscal number)"
};

const normalizeCountry = (country) => (country ? String(country).trim().toUpperCase() : "");

/** What the tax number field is labelled for a country. */
export const getTaxIdLabel = (country) => TAX_ID_LABELS[normalizeCountry(country)] ?? "Tax ID";

/**
 * The type a tax number is sent under.
 *
 * `saved` is the profile as the API last returned it. A type the server settled
 * on for the same country is kept — it knows which of a country's several types
 * the number is — but one saved for a different country is dropped, so moving an
 * address out of Kosovo never leaves `xk_nui` behind, and moving it in always
 * sets it. Undefined means "infer it".
 */
export const resolveTaxIdType = (country, saved) => {
   const code = normalizeCountry(country);
   if (!code) return undefined;

   if (TAX_ID_TYPES[code]) return TAX_ID_TYPES[code];

   const savedCountry = normalizeCountry(saved?.address?.country);
   const savedType = saved?.taxId?.type;

   return savedCountry === code && savedType && savedType !== KOSOVO_TAX_ID_TYPE
      ? savedType
      : undefined;
};

/** The `taxId` block for PUT /Payment/billing-profile — null when there is no number. */
export const buildTaxId = (country, value, saved) => {
   const number = typeof value === "string" ? value.trim() : "";
   if (!number) return null;

   const type = resolveTaxIdType(country, saved);
   return type ? { type, value: number } : { value: number };
};
